import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Filter } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Empty } from "@/components/Empty";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { STOCK_STATUS_TONE, STOCK_STATUS_LABEL, VDB_EDIT_ROLES } from "@/lib/constants";
import { useWarehouse } from "@/contexts/WarehouseContext";
import { useRole } from "@/contexts/RoleContext";
import { VdbEditDialog } from "@/components/VdbEditDialog";
import { KipkruidenCell, VdbCell } from "@/components/WarehouseStockCells";
import { formatCurrency, formatDate, formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";

const STOCK_FILTERS = [
  { id: "", label: "Alle producten" },
  { id: "op_voorraad", label: "Voldoende voorraad" },
  { id: "laag", label: "Lage voorraad" },
  { id: "niet_op_voorraad", label: "Niet op voorraad" },
  { id: "te_bestellen", label: "Moet besteld worden" },
  { id: "in_bestelling", label: "In bestelling" },
];

export default function Voorraad() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [stockStatus, setStockStatus] = useState(params.get("stock_status") || "");
  const { warehouse: activeWh } = useWarehouse();
  const [meta, setMeta] = useState({ categories: [], live: false });
  const [editing, setEditing] = useState(null);
  const { role } = useRole();
  const canEditVdb = VDB_EDIT_ROLES.includes(role.id);

  useEffect(() => { api.get("/meta/categories").then((r) => setMeta(r.data)); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/products", {
        params: {
          search: search || undefined,
          category: category || undefined,
          stock_status: stockStatus || undefined,
        },
      });
      setProducts(r.data);
    } finally { setLoading(false); }
  }, [search, category, stockStatus]);

  useEffect(() => {
    load();
    const q = {};
    if (search) q.search = search;
    if (category) q.category = category;
    if (stockStatus) q.stock_status = stockStatus;
    setParams(q, { replace: true });
  }, [load, search, category, stockStatus, setParams]);

  const totalValue = useMemo(
    () => products.reduce((s, p) => s + p.stock_total * p.cost_price, 0),
    [products]
  );

  return (
    <div className="px-4 md:px-8 lg:px-12 py-8 md:py-10" data-testid="inventory-page">
      <PageHeader
        eyebrow="Voorraad"
        title="Voorraadoverzicht"
        description={meta.live
          ? "Alle producten uit kipkruiden.nl. Totaal = Kipkruiden (Hoogeveenenweg 73) + Van den Brink (Zeewolde, handmatig ingevuld)."
          : "Alle producten met voorraad per magazijn, gereserveerd en beschikbaar."}
        action={!meta.live && (
          <div className="hidden md:block text-right">
            <div className="label-eyebrow">Voorraadwaarde</div>
            <div className="font-mono text-2xl font-semibold">{formatCurrency(totalValue)}</div>
          </div>
        )}
      />

      {/* Zoeken + categorie */}
      <div className="surface p-4 md:p-6 mb-4 flex flex-col gap-3">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input data-testid="inventory-search" placeholder="Zoek op productnaam, artikelnummer of opslaglocatie…"
            value={search} onChange={(e) => setSearch(e.target.value)} className="pl-11 h-12 text-base" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="filter-category-all" variant={category === "" ? "default" : "outline"}
            onClick={() => setCategory("")} className="h-12">Alle categorieën</Button>
          {meta.categories.map((c) => (
            <Button key={c} data-testid={`filter-category-${c}`} variant={category === c ? "default" : "outline"}
              onClick={() => setCategory(c === category ? "" : c)} className="h-12">{c}</Button>
          ))}
        </div>
      </div>

      {/* Status filter */}
      <div className="surface p-4 mb-6 flex flex-wrap gap-2 items-center" data-testid="stock-filter-bar">
        <span className="label-eyebrow flex items-center gap-2"><Filter className="w-4 h-4" />Status</span>
        {STOCK_FILTERS.map((opt) => (
          <Button key={opt.id || "any"} data-testid={`stock-filter-${opt.id || "any"}`}
            variant={stockStatus === opt.id ? "default" : "outline"}
            onClick={() => setStockStatus(opt.id)} size="sm" className="h-10">
            {opt.label}
          </Button>
        ))}
      </div>

      {/* Tabel */}
      <div className="surface overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-base">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-4">Status</th>
                <th className="px-4 py-4">Artikel</th>
                <th className="px-4 py-4">Productnaam</th>
                <th className="px-4 py-4">Categorie</th>
                <th className="px-4 py-4 text-right">Voorraad totaal</th>
                <th className={cn("px-4 py-4 text-right", activeWh === "kipkruiden" && "text-primary bg-primary/10")} data-testid="col-kipkruiden">Kipkruiden</th>
                <th className={cn("px-4 py-4 text-right", activeWh === "vandenbrink" && "text-primary bg-primary/10")} data-testid="col-vandenbrink">Van den Brink</th>
                <th className="px-4 py-4 text-right">Gereserveerd</th>
                <th className="px-4 py-4 text-right">Beschikbaar hier</th>
                <th className="px-4 py-4 text-right">Minimum</th>
                <th className="px-4 py-4 text-right">Te bestellen</th>
                <th className="px-4 py-4 text-right">In bestelling</th>
                <th className="px-4 py-4">Opslag</th>
                <th className="px-4 py-4">THT</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk-${i}`}><td colSpan={14} className="px-4 py-5"><div className="h-10 bg-muted/40 animate-pulse rounded-md" /></td></tr>
              )) : products.map((p) => (
                <tr key={p.id} data-testid={`product-row-${p.article_number}`}
                  onClick={() => navigate(`/producten/${p.id}`)}
                  className="hover:bg-accent transition-colors cursor-pointer">
                  <td className="px-4 py-4"><StatusBadge tone={STOCK_STATUS_TONE[p.stock_status]}>{STOCK_STATUS_LABEL[p.stock_status]}</StatusBadge></td>
                  <td className="px-4 py-4 font-mono text-sm">{p.article_number}</td>
                  <td className="px-4 py-4 font-medium">{p.name}</td>
                  <td className="px-4 py-4 text-muted-foreground">{p.category}</td>
                  <td className="px-4 py-4 text-right font-mono font-semibold">
                    {p.stock_managed === false ? "—" : formatNumber(p.stock_total, 1)} <span className="text-muted-foreground text-xs">{p.unit}</span>
                  </td>
                  <td className="px-4 py-4 text-right"><KipkruidenCell p={p} /></td>
                  <td className="px-4 py-4 text-right"><VdbCell p={p} canEdit={canEditVdb && p.source === "woocommerce"} onEdit={setEditing} /></td>
                  <td className="px-4 py-4 text-right font-mono text-muted-foreground">{p.stock_managed === false ? "—" : formatNumber(p.reserved_stock, 1)}</td>
                  <td className={cn("px-4 py-4 text-right font-mono font-semibold",
                    p.stock_status === "kritiek" && "text-status-critical",
                    p.stock_status === "laag" && "text-status-low")}>
                    {p.stock_managed === false ? "—" : formatNumber(p.available_stock, 1)}
                  </td>
                  <td className="px-4 py-4 text-right font-mono text-muted-foreground">{formatNumber(p.minimum_stock, 0)}</td>
                  <td className="px-4 py-4 text-right font-mono">
                    {p.te_bestellen > 0 ? <span className="text-status-critical">{formatNumber(p.te_bestellen, 0)}</span> : "—"}
                  </td>
                  <td className="px-4 py-4 text-right font-mono">
                    {p.open_ordered > 0 ? <span className="text-status-in-transit">{formatNumber(p.open_ordered, 0)}</span> : "—"}
                  </td>
                  <td className="px-4 py-4 font-mono text-xs">
                    {p.primary_warehouse === "kipkruiden" ? p.storage_location_kk : p.storage_location_vdb}
                  </td>
                  <td className="px-4 py-4 text-sm text-muted-foreground">{formatDate(p.expiry_date)}</td>
                </tr>
              ))}
              {!loading && products.length === 0 && (
                <tr><td colSpan={14} className="py-12"><Empty title="Geen producten gevonden voor deze filters." /></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <VdbEditDialog product={editing} open={!!editing} onOpenChange={(o) => !o && setEditing(null)}
        onSaved={() => load()} />
    </div>
  );
}
